import React from 'react'
import Downshift from 'downshift'
import { Button, Flex, HighlightedText, Icon } from '@ui'
import { Classes, InputGroup, Intent, MenuItem, Menu } from '@blueprintjs/core'
import UpdateUsageSection from './UpdateUsageSection'
import { IFloorMapSpace } from '@api/types'
import { INeighborhood, IUsageType } from '@api'
import { get } from 'lodash'
import { isNeighborhood } from '@utils'
import { useReload } from '@hooks/reloadContext'
import useNeighborhoodsSearch from 'components/NeighborhoodManagement/hooks/useNeighborhoodsSearch'
import css from './spaceAssignmentSelect.module.scss'

interface NeighborhoodTabProps {
  floorId: string
  space?: IFloorMapSpace
  spaces?: IFloorMapSpace[]
  updateSpaceUsage: (usageType: IUsageType) => void
  addToNeighborhood: (neighborhood: INeighborhood) => Promise<void> | undefined
  removeFromNeighborhood: () => void
}

export default function NeighborhoodTab({
  floorId,
  space,
  spaces,
  updateSpaceUsage,
  addToNeighborhood,
  removeFromNeighborhood,
}: NeighborhoodTabProps) {
  if (space && isNeighborhood(space.usageType)) {
    return (
      <div style={{ margin: '-10px -5px' }}>
        {get(space, 'spaceAssignment.neighborhood') && (
          <div style={{ margin: '5px' }}>
            <Button
              fill
              minimal
              ghost
              intent={Intent.DANGER}
              iconName="times"
              text="Unassign"
              onClick={removeFromNeighborhood}
            />
          </div>
        )}
        <div
          style={{
            height: get(space, 'spaceAssignment.neighborhood') ? 250 : 287,
          }}
        >
          <NeighborhoodList {...{ floorId, space, addToNeighborhood }} />
        </div>
      </div>
    )
  }

  if (spaces && spaces.every(({ usageType }) => isNeighborhood(usageType))) {
    return (
      <div style={{ margin: '-10px -5px' }}>
        {spaces.some((s) => get(s, 'spaceAssignment.neighborhood')) && (
          <div style={{ margin: '5px' }}>
            <Button
              fill
              minimal
              ghost
              intent={Intent.DANGER}
              iconName="times"
              text="Unassign"
              onClick={removeFromNeighborhood}
            />
          </div>
        )}
        <div
          style={{
            height: get(space, 'spaceAssignment.neighborhood') ? 250 : 287,
          }}
        >
          <NeighborhoodList {...{ floorId, space, addToNeighborhood }} />
        </div>
      </div>
    )
  }

  return (
    <UpdateUsageSection
      text="To assign this space to a neighborhood convert it to:"
      filter={(usageType) => isNeighborhood(usageType)}
      updateSpaceUsage={updateSpaceUsage}
    />
  )
}

function NeighborhoodList({
  floorId,
  space,
  addToNeighborhood,
}: Pick<NeighborhoodTabProps, 'floorId' | 'space' | 'addToNeighborhood'>) {
  const search = useNeighborhoodsSearch(floorId)

  const reload = useReload()

  return (
    <Downshift
      itemToString={() => ''}
      onSelect={(neighborhood) => {
        addToNeighborhood(neighborhood)?.then?.(() => reload?.neighborhoods())
        // when selecting a neighborhood
        // allocate && allocate(neighborhood)
      }}
    >
      {({ getInputProps, getMenuProps, getItemProps, highlightedIndex }) => (
        <div
          style={{
            height: '100% ',
            position: 'relative',
            overflowY: 'auto',
            borderRadius: 4,
            width: 280,
          }}
        >
          <div
            style={{
              position: 'sticky',
              top: 0,
              padding: 5,
              paddingBottom: 0,
              background: 'white',
              zIndex: 1,
            }}
          >
            {/* eslint-disable @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            <InputGroup
              {...getInputProps({
                autoFocus: true,
                className: Classes.FILL,
                value: search.query,
                onChange: ({ currentTarget: { value } }: React.FormEvent<HTMLInputElement>) => {
                  search.run(value)
                },
                placeholder: 'Search...',
                leftIcon: <Icon name="search" />,
                onFocus: ({ currentTarget }: React.FormEvent<HTMLInputElement>) => {
                  currentTarget.select()
                },
                style: { marginBottom: 5 },
              })}
            />
          </div>
          <div {...getMenuProps()}>
            {search.loading && (
              <Menu>
                <MenuItem
                  disabled
                  className={Classes.SKELETON}
                  text="placeholder"
                  style={{ marginBottom: 5 }}
                />
                <MenuItem
                  disabled
                  className={Classes.SKELETON}
                  text="placeholder"
                  style={{ marginBottom: 5 }}
                />
                <MenuItem
                  disabled
                  className={Classes.SKELETON}
                  text="placeholder"
                  style={{ marginBottom: 5 }}
                />
              </Menu>
            )}
            {!search.loading && !search.items.length ? (
              <Menu>
                <MenuItem disabled text="No results" />
              </Menu>
            ) : (
              <Menu>
                {search.items.map((neighborhood, i) => (
                  <MenuItem
                    {...getItemProps({
                      key: neighborhood.id,
                      item: neighborhood,
                    })}
                    active={i === highlightedIndex}
                    disabled={neighborhood.id === get(space, 'spaceAssignment.neighborhood.id')}
                    text={<NeighborhoodItem {...neighborhood} query={search.query} />}
                  />
                ))}
              </Menu>
            )}
          </div>
        </div>
      )}
    </Downshift>
  )
}

export function NeighborhoodItem({ name, color, query }: INeighborhood & { query?: string }) {
  return (
    <Flex align="center">
      <div className={css.square} style={{ backgroundColor: `#${color}` }} />
      <HighlightedText text={name} query={query} />
    </Flex>
  )
}
